import { useEffect, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import { questionBank, type AnatomyQuestion } from '../data/functionalAnatomyQuestionBank';

/* ------------------------------------------------------------------ */
/*  기능해부학 챌린지 — 5단계 레벨 테스트                                 */
/*  원본 standalone HTML(stampfit_functional_anatomy_v19_final_fact_checked.html)의 */
/*  화면(레드 #b4232c 테마)과 채점/출제 로직을 그대로 이식.                */
/*  원본이 DOM을 직접 조작하는 바닐라 JS 앱이라, 별도 컨테이너에 마운트된  */
/*  ref 안에서 동일한 로직을 그대로 실행하는 방식으로 포팅(전역 id/함수는  */
/*  이 컴포넌트 전용 네임스페이스로 스코프). 원본의 중복 사이트 헤더(로고) */
/*  와 화면폭 고정(.shell) 은 앱 전역 헤더·탭바와 충돌해 생략.            */
/* ------------------------------------------------------------------ */

interface QuizState {
  stage: number;
  index: number;
  current: AnatomyQuestion[];
  scores: number[];
  totalCorrect: number;
  answered: number;
  wrong: (AnatomyQuestion & { selected: number })[];
  used: Set<string>;
}

const STAGE_INFO: Record<number, [string, string]> = {
  1: ['뼈·관절·면·축', 'Bones, Joints, Planes & Axes'],
  2: ['상지·코어 기능해부학', 'Upper-Extremity & Core Anatomy'],
  3: ['하지 기능해부학', 'Lower-Extremity Anatomy'],
  4: ['운동학', 'Kinesiology'],
  5: ['실전 기능해부학', 'Practical Functional Anatomy'],
};

export default function FunctionalAnatomyChallenge({ onBack }: { onBack: () => void }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const $ = (sel: string) => container.querySelector(sel) as HTMLElement;
    const stageInfo = STAGE_INFO;

    let state: QuizState = {
      stage: 0,
      index: 0,
      current: [],
      scores: [],
      totalCorrect: 0,
      answered: 0,
      wrong: [],
      used: new Set(),
    };
    const app = $('#fa-quiz-app');

    function shuffle<T>(a: T[]): T[] {
      return [...a].sort(() => Math.random() - 0.5);
    }

    function renderSide() {
      let h = '';
      for (let i = 1; i <= 5; i++) {
        let cls = i === state.stage ? 'active' : i < state.stage ? 'done' : 'locked';
        if (state.stage === 0) cls = 'locked';
        h += `<div class="stage ${cls}"><b>STEP ${i}. ${stageInfo[i][0]}</b><span>${stageInfo[i][1]} · </span></div>`;
      }
      $('#fa-quiz-stageList').innerHTML = h;
      $('#fa-quiz-totalCorrect').textContent = String(state.totalCorrect);
      $('#fa-quiz-answered').textContent = String(state.answered);
    }

    function intro() {
      state.stage = 0;
      renderSide();
      app.innerHTML = `<div class="eyebrow">STAMP FIT ORIGINAL ASSESSMENT</div><h1>기능해부학을<br>암기에서 추론까지</h1><p class="lead">뼈·관절·면·축에서 시작해 상지·코어와 하지 근육 해부학, 운동학과 실전 기능해부학까지 단계적으로 확인합니다.</p><div class="rules"><div class="rule"><b>전체 과정</b><span>단계별 문항 문제은행</span></div><div class="rule"><b>단계별 랜덤 10문항</b><span>한 시험 내 중복 출제 없음</span></div><div class="rule"><b>7문항 이상 통과</b><span>미달 시 해당 단계에서 종료</span></div></div><button class="btn" onclick="window.__faQuizStartStage(1)">테스트 시작</button><details class="refs"><summary>문항 구성 참고자료</summary><p>Moore, Clinically Oriented Anatomy · Gray's Anatomy for Students · Neumann, Kinesiology of the Musculoskeletal System · Kendall, Muscles: Testing and Function · Magee, Orthopedic Physical Assessment<br>본 평가는 STAMP FIT에서 학습 목적으로 자체 구성한 문항이며 특정 교재의 문제를 그대로 인용하지 않았습니다.</p></details>`;
    }

    function normalizeAnswer(q: AnatomyQuestion) {
      const raw = (q.choices?.[q.answer] || '').toLowerCase();
      return raw.replace(/\([^)]*\)/g, '').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
    }

    function questionType(q: AnatomyQuestion) {
      const t = ((q.category || '') + ' ' + (q.question || '')).toLowerCase();
      if (t.includes('기시')) return 'origin';
      if (t.includes('정지')) return 'insertion';
      if (t.includes('신경')) return 'innervation';
      if (t.includes('작용') || t.includes('움직임') || t.includes('운동')) return 'action';
      if (t.includes('명칭') || t.includes('근육은')) return 'name';
      if (t.includes('면')) return 'plane';
      if (t.includes('축')) return 'axis';
      if (t.includes('관절')) return 'joint';
      return q.category || 'other';
    }

    function subjectKey(q: AnatomyQuestion) {
      const t = ((q.topicKey || '') + ' ' + (q.question || '') + ' ' + (q.choices?.[q.answer] || '')).toLowerCase();
      const subjects = [
        '대둔근', '중둔근', '소둔근', '장요근', '대요근', '이상근', '대퇴직근', '외측광근', '내측광근', '중간광근',
        '대퇴이두근', '반건양근', '반막양근', '대내전근', '장내전근', '단내전근', '박근', '봉공근', '비복근', '가자미근',
        '전경골근', '후경골근', '장비골근', '단비골근', '삼각근', '극상근', '극하근', '소원근', '견갑하근', '승모근',
        '전거근', '광배근', '대흉근', '소흉근', '능형근', '상완이두근', '상완삼두근', '복직근', '외복사근', '내복사근',
        '복횡근', '다열근', '척추기립근', '요방형근', '횡격막', '골반저근', '견갑골', '견관절', '고관절', '무릎', '슬관절',
        '발목', '족관절', '척추', '골반', '팔꿈치', '주관절', '손목', '완관절', '스쿼트', '런지', '데드리프트', '보행',
        '푸시업', '오버헤드',
      ];
      for (const s of subjects) {
        if (t.includes(s)) return s;
      }
      return (q.topicKey || q.id).split('_').slice(0, 2).join('_');
    }

    function diversifiedSelect(pool: AnatomyQuestion[], count: number) {
      const candidates = shuffle(pool);
      const selected: AnatomyQuestion[] = [];
      const usedIds = new Set<string>();
      const subjectCounts = new Map<string, number>();
      const answerCounts = new Map<string, number>();
      const passes = [
        { subject: 1, answer: 1, typeAdj: true, answerAdj: true },
        { subject: 1, answer: 2, typeAdj: true, answerAdj: true },
        { subject: 2, answer: 2, typeAdj: true, answerAdj: true },
        { subject: 2, answer: 2, typeAdj: false, answerAdj: true },
        { subject: 3, answer: 3, typeAdj: false, answerAdj: false },
      ];
      for (const rule of passes) {
        for (const q of candidates) {
          if (selected.length >= count) break;
          if (usedIds.has(q.id)) continue;
          const sk = subjectKey(q), ak = normalizeAnswer(q), tk = questionType(q);
          if ((subjectCounts.get(sk) || 0) >= rule.subject) continue;
          if ((answerCounts.get(ak) || 0) >= rule.answer) continue;
          if (selected.length) {
            const prev = selected[selected.length - 1];
            if (rule.typeAdj && questionType(prev) === tk) continue;
            if (rule.answerAdj && normalizeAnswer(prev) === ak) continue;
            if (subjectKey(prev) === sk) continue;
          }
          selected.push(q);
          usedIds.add(q.id);
          subjectCounts.set(sk, (subjectCounts.get(sk) || 0) + 1);
          answerCounts.set(ak, (answerCounts.get(ak) || 0) + 1);
        }
        if (selected.length >= count) break;
      }
      if (selected.length < count) {
        for (const q of candidates) {
          if (selected.length >= count) break;
          if (!usedIds.has(q.id)) {
            selected.push(q);
            usedIds.add(q.id);
          }
        }
      }
      for (let i = 1; i < selected.length; i++) {
        const prev = selected[i - 1];
        if (
          normalizeAnswer(prev) === normalizeAnswer(selected[i]) ||
          questionType(prev) === questionType(selected[i]) ||
          subjectKey(prev) === subjectKey(selected[i])
        ) {
          const j = selected.findIndex(
            (q, k) =>
              k > i &&
              normalizeAnswer(q) !== normalizeAnswer(prev) &&
              questionType(q) !== questionType(prev) &&
              subjectKey(q) !== subjectKey(prev)
          );
          if (j > i) [selected[i], selected[j]] = [selected[j], selected[i]];
        }
      }
      return selected.slice(0, count);
    }

    function startStage(n: number) {
      state.stage = n;
      state.index = 0;
      const pool = questionBank.filter((q) => q.level === n && !state.used.has(q.id));
      state.current = diversifiedSelect(pool, 10);
      state.current.forEach((q) => state.used.add(q.id));
      state.scores[n - 1] = 0;
      renderSide();
      showQuestion();
    }

    function showQuestion() {
      renderSide();
      const q = state.current[state.index];
      const pct = (state.index / 10) * 100;
      app.innerHTML = `<div class="head"><div><span class="badge">STEP ${state.stage} · ${stageInfo[state.stage][0]}</span><div class="cat">${q.category}</div></div><b>${state.index + 1} / 10</b></div><div class="progress"><div class="fill" style="width:${pct}%"></div></div><div class="q">${q.question}</div><div class="answers">${q.choices.map((c, i) => `<button class="answer" onclick="window.__faQuizAnswer(${i})"><span class="key">${i + 1}</span><span>${c}</span></button>`).join('')}</div>`;
    }

    function explanationContext(q: AnatomyQuestion) {
      const cat = q.category || '';
      const text = (q.question || '') + ' ' + cat;
      const level = q.level || 0;
      if (/신경지배|Innervation/.test(text)) return '신경지배 문항은 해당 근육에 운동신호를 전달하는 말초신경을 구분하는 문제입니다. 근육의 위치나 작용이 비슷해도 신경지배는 해부학적 구획과 신경 경로에 따라 달라집니다.';
      if (/기시|Origin/.test(text)) return '기시는 일반적으로 수축 시 상대적으로 고정되는 쪽의 부착부를 뜻합니다. 다만 실제 움직임에서는 열린사슬·닫힌사슬 조건에 따라 어느 쪽이 움직이는지는 달라질 수 있습니다.';
      if (/정지|Insertion/.test(text)) return '정지는 일반적으로 수축 시 기시 쪽으로 가까워지는 부착부입니다. 답을 고를 때는 같은 부위의 인접 근육 부착점과 혼동하지 않는 것이 중요합니다.';
      if (/운동면|Plane|축|Axis|방향/.test(text)) return '운동면과 축은 서로 수직 관계입니다. 시상면 운동은 관상축, 관상면 운동은 시상축, 수평면 운동은 수직축을 중심으로 일어납니다.';
      if (/관절운동|Joint Action|굴곡|신전|외전|내전|회전/.test(text) && level === 1) return '관절운동은 해부학적 자세를 기준으로 정의합니다. 해당 근육이 관절의 어느 쪽을 지나가는지와 섬유 방향을 함께 보면 작용을 이해하기 쉽습니다.';
      if (level === 2) return '상지와 코어 근육의 명칭, 기시, 정지, 작용, 신경지배를 함께 연결해 학습합니다. 코어는 복벽, 척추 주위, 호흡 및 골반저의 기능을 포함합니다.';
      if (level === 3) return '운동학적 해석에서는 관절운동만이 아니라 모멘트암, 외부저항의 방향, 열린사슬·닫힌사슬 조건, 길이-장력 관계를 함께 고려해야 합니다.';
      if (/Special Test|스페셜|검사|Sign|Test/.test(text) && level >= 4) return '스페셜 테스트는 특정 구조나 기능의 관여 가능성을 높이거나 낮추는 자료입니다. 단일 검사만으로 확진하지 않으며 병력, 통증 위치, 가동범위, 근력 및 다른 검사와 함께 해석해야 합니다.';
      if (level === 4) return '관찰 소견은 근육 하나의 문제로 단정하지 않고, 관절 가동성·근력·운동조절·통증 반응을 함께 비교해야 합니다. 이 문항의 정답은 제시된 정보와 가장 직접적으로 연결되는 기능해부학적 해석입니다.';
      if (level === 5) return '실전 기능해부학에서는 여러 소견을 하나의 기능적 가설로 묶되, 가설을 확정 진단처럼 받아들이지 않아야 합니다. 보조 동작이나 재검사를 통해 소견이 실제로 변하는지 확인하는 과정이 필요합니다.';
      return '정답은 질문에서 요구한 해부학적 범주와 가장 직접적으로 일치하는 보기입니다.';
    }

    function applicationPoint(q: AnatomyQuestion) {
      const t = (q.question || '') + ' ' + (q.explanation || '');
      if (/스쿼트|Squat/.test(t)) return '스쿼트에서는 발-무릎-고관절의 정렬과 하강·상승 구간을 나누어 관찰합니다.';
      if (/런지|Lunge|스플릿 스쿼트/.test(t)) return '런지 계열은 앞다리와 뒷다리의 역할이 다르므로 어느 다리와 어느 구간을 묻는지 확인해야 합니다.';
      if (/푸시업|Push-up|플랭크|Plank/.test(t)) return '체중지지 상지 동작에서는 상완골 움직임뿐 아니라 견갑골의 전인·상방회전·흉곽 고정도 함께 봅니다.';
      if (/호흡|들숨|날숨|횡격막|늑간/.test(t)) return '조용한 호흡과 강제 호흡을 구분해야 합니다. 조용한 날숨은 대체로 수동적이며, 강제 날숨에는 복근과 내늑간근의 기여가 커집니다.';
      if (/오버헤드|Overhead|팔을 들|어깨 외전/.test(t)) return '팔 거상은 상완와관절 움직임과 견갑골 상방회전·후방경사, 흉추 신전이 협응해야 합니다.';
      if (/보행|걷기|달리기|푸시오프/.test(t)) return '보행과 달리기는 시기별 역할이 다르므로 초기접촉, 입각기, 말기입각기, 유각기를 구분해 해석합니다.';
      if (/싱글레그|한발|Single Leg|Trendelenburg/.test(t)) return '한발 지지에서는 지지측 고관절 외전근과 체간·발목의 협응을 함께 평가합니다.';
      return '실제 적용 시에는 통증, 가동범위, 저항 방향, 운동 구간을 함께 확인하면 같은 개념을 다른 동작에도 연결할 수 있습니다.';
    }

    function detailedExplanation(q: AnatomyQuestion, selected: number) {
      const correct = q.choices[q.answer];
      const chosen = q.choices[selected];
      const t = ((q.category || '') + ' ' + (q.question || '')).toLowerCase();
      let selectedReason = '';
      if (selected === q.answer) {
        selectedReason = '선택한 보기가 질문에서 요구한 해부학적·운동학적 관계와 일치합니다.';
      } else if (/신경지배|innervation/.test(t)) {
        selectedReason = `선택한 <b>${chosen}</b>은(는) 이 문항에서 묻는 근육의 주된 신경지배와 일치하지 않습니다. 정답은 <b>${correct}</b>입니다.`;
      } else if (/기시|origin/.test(t)) {
        selectedReason = `선택한 <b>${chosen}</b>은(는) 해당 근육의 기시 부착점과 일치하지 않습니다. 정답은 <b>${correct}</b>입니다.`;
      } else if (/정지|insertion/.test(t)) {
        selectedReason = `선택한 <b>${chosen}</b>은(는) 해당 근육의 정지 부착점과 일치하지 않습니다. 정답은 <b>${correct}</b>입니다.`;
      } else if (/작용|action|굴곡|신전|외전|내전|회전|회내|회외/.test(t)) {
        selectedReason = `선택한 <b>${chosen}</b>은(는) 제시된 근육·관절의 대표적인 작용 또는 해당 상황의 운동 방향과 일치하지 않습니다. 정답은 <b>${correct}</b>입니다.`;
      } else if (q.level >= 5) {
        selectedReason = `선택한 <b>${chosen}</b>은(는) 제시된 소견만으로는 정답보다 직접적인 해부학적 설명이 되기 어렵습니다. 이 문항에서 가장 직접적으로 연결되는 답은 <b>${correct}</b>입니다. 실제 현장에서는 병력과 여러 평가 결과를 함께 확인해야 합니다.`;
      } else {
        selectedReason = `선택한 <b>${chosen}</b>은(는) 질문에서 요구한 구조·운동학적 관계와 일치하지 않습니다. 정답은 <b>${correct}</b>입니다.`;
      }
      return `<div class="explain-box"><div><span class="explain-label">핵심 해설</span><strong>${q.explanation}</strong></div><div class="explain-section"><span class="explain-label">정답 근거</span>${explanationContext(q)}</div><div class="explain-section ${selected === q.answer ? 'correct-choice' : 'wrong-choice'}"><span class="explain-label">선택지 분석</span>${selectedReason}</div><div class="explain-section"><span class="explain-label">적용 포인트</span>${applicationPoint(q)}</div>${q.level >= 4 ? '<div class="explain-section caution"><span class="explain-label">주의</span>이 해석은 교육용 기능적 가설입니다. 실제 현장에서는 병력, 증상, 가동범위, 근력 및 여러 평가 결과를 함께 확인해야 합니다.</div>' : ''}</div>`;
    }

    function answer(i: number) {
      const q = state.current[state.index];
      const buttons = [...container!.querySelectorAll('.answer')] as HTMLButtonElement[];
      buttons.forEach((b) => (b.disabled = true));
      buttons[q.answer].classList.add('correct');
      const ok = i === q.answer;
      if (!ok) buttons[i].classList.add('wrong');
      state.answered++;
      if (ok) {
        state.scores[state.stage - 1]++;
        state.totalCorrect++;
      } else {
        state.wrong.push({ ...q, selected: i });
      }
      const fb = document.createElement('div');
      fb.className = 'feedback ' + (ok ? 'ok' : 'no');
      fb.innerHTML = `<b>${ok ? '정답입니다.' : '오답입니다.'}</b>${ok ? `<br>${q.explanation}` : detailedExplanation(q, i)}`;
      app.appendChild(fb);
      const n = document.createElement('button');
      n.className = 'btn next';
      n.textContent = state.index === 9 ? '단계 결과 확인' : '다음 문제';
      n.onclick = next;
      app.appendChild(n);
      renderSide();
    }

    function next() {
      state.index++;
      if (state.index < 10) showQuestion();
      else finishStage();
    }

    function finishStage() {
      const score = state.scores[state.stage - 1];
      if (score >= 7 && state.stage < 5) {
        showModal('🎯', `STEP ${state.stage} 통과`, `${score}/10 정답입니다. 다음 단계로 진행합니다.`, () => startStage(state.stage + 1));
        return;
      }
      finishTest();
    }

    function getGrade() {
      const failed = state.scores.findIndex((s) => s < 7);
      if (state.stage === 5 && state.scores.length === 5 && state.totalCorrect >= 48) return '기능해부학의 신';
      if (failed === 0) return '기능해부학 교육생';
      if (failed === 1) return '기능해부학 강사 준비생';
      if (failed === 2) return '기능해부학 강사';
      if (failed === 3) return '기능해부학 전문가';
      return '기능해부학 마스터';
    }

    function finishTest() {
      const grade = getGrade();
      const low = ['기능해부학 교육생', '기능해부학 강사 준비생'].includes(grade);
      const god = grade === '기능해부학의 신';
      const msg = low ? `현재 등급은 ${grade} 수준입니다. STAMP FIT과 함께 조금 더 노력해보세요!` : `현재 등급은 ${grade}입니다. 지금까지의 학습 성과를 축하합니다!`;
      showModal(
        god ? '👑' : low ? '📘' : '🎉',
        god ? '기능해부학의 신 달성!' : '현재 등급 안내',
        god ? '총 50문항 중 48문항 이상 정답을 기록했습니다. STAMP FIT 기능해부학 최고 등급을 축하합니다!' : msg,
        showResult
      );
    }

    function showResult() {
      const grade = getGrade();
      renderSide();
      app.innerHTML = `<div class="eyebrow">FINAL RESULT</div><h1>평가 결과</h1><span class="grade">${grade}</span><p class="lead">총 ${state.answered}문항 중 ${state.totalCorrect}문항 정답</p><div class="summary">${[1, 2, 3, 4, 5].map((i) => `<div class="sum"><span>STEP ${i}</span><strong>${state.scores[i - 1] ?? '-'}</strong><small>/ 10</small></div>`).join('')}</div><button class="btn" onclick="window.__faQuizResetTest()">다시 시작</button><div class="wrong-note"><h2>오답노트</h2>${state.wrong.length ? state.wrong.map((w, k) => `<div class="wrong-item"><b>${k + 1}. ${w.question}</b><p>선택: ${w.choices[w.selected]}</p><p>정답: ${w.choices[w.answer]}</p>${detailedExplanation(w, w.selected)}</div>`).join('') : '<p class="lead">오답이 없습니다.</p>'}</div><details class="refs"><summary>문항 구성 참고자료</summary><p>Neumann · Kendall · McGill · Magee · Gray's Anatomy for Students · Trail Guide to the Body · NASM CES · ACSM Guidelines<br>본 평가는 STAMP FIT에서 학습 목적으로 자체 구성한 문항이며 특정 교재의 문제를 그대로 인용하지 않았습니다.</p></details>`;
    }

    function showModal(icon: string, title: string, msg: string, cb?: () => void) {
      const m = $('#fa-quiz-modal');
      m.innerHTML = `<div class="modal"><div class="modal-box"><div class="modal-icon">${icon}</div><h2>${title}</h2><p>${msg}</p><button class="btn" id="fa-quiz-modalBtn">확인</button></div></div>`;
      ($('#fa-quiz-modalBtn') as HTMLButtonElement).onclick = () => {
        m.innerHTML = '';
        cb && cb();
      };
    }

    function resetTest() {
      state = { stage: 0, index: 0, current: [], scores: [], totalCorrect: 0, answered: 0, wrong: [], used: new Set() };
      intro();
    }

    (window as any).__faQuizStartStage = startStage;
    (window as any).__faQuizAnswer = answer;
    (window as any).__faQuizResetTest = resetTest;

    intro();

    return () => {
      delete (window as any).__faQuizStartStage;
      delete (window as any).__faQuizAnswer;
      delete (window as any).__faQuizResetTest;
    };
  }, []);

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>챌린지 목록으로</span>
      </button>

      <div ref={containerRef} className="fa-quiz">
        <style>{`
          .fa-quiz{
            --red:#b4232c;--red2:#861820;--bg:#f5f6f8;--card:#fff;--text:#18202b;
            --muted:#667085;--line:#e5e7eb;--green:#147a4b;--soft:#fff1f2;
            background:radial-gradient(circle at 8% 8%,rgba(180,35,44,.10),transparent 28%),var(--bg);
            font-family:Pretendard,"Noto Sans KR",Arial,sans-serif;
            color:var(--text);
            border-radius:24px;
            padding:24px;
            margin-bottom:24px;
          }
          .fa-quiz,.fa-quiz *{box-sizing:border-box}
          .fa-quiz .layout{display:grid;grid-template-columns:310px 1fr;gap:22px}
          .fa-quiz .side,.fa-quiz .card{background:rgba(255,255,255,.96);border-radius:24px;box-shadow:0 20px 55px rgba(30,38,50,.12)}
          .fa-quiz .side{padding:22px;position:sticky;top:7.5rem;height:max-content}
          .fa-quiz .card{padding:30px;min-width:0}
          .fa-quiz .eyebrow{font-size:12px;color:var(--red);font-weight:900;letter-spacing:.1em}
          .fa-quiz .stage-list{display:grid;gap:9px;margin:18px 0}
          .fa-quiz .stage{padding:12px;border:1px solid var(--line);border-radius:14px}
          .fa-quiz .stage.active{border-color:var(--red);background:var(--soft)}
          .fa-quiz .stage.done{border-color:#77c39c;background:#ecfdf3}
          .fa-quiz .stage.locked{opacity:.52}
          .fa-quiz .stage b{display:block}
          .fa-quiz .stage span{font-size:12px;color:var(--muted)}
          .fa-quiz .stats{display:grid;grid-template-columns:1fr 1fr;gap:9px}
          .fa-quiz .stat{padding:11px;background:#f8fafc;border-radius:13px}
          .fa-quiz .stat strong{display:block;font-size:22px}
          .fa-quiz .stat span{font-size:11px;color:var(--muted)}
          .fa-quiz h1{font-size:38px;line-height:1.18;margin:10px 0}
          .fa-quiz .lead{color:var(--muted);line-height:1.7}
          .fa-quiz .rules{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin:24px 0}
          .fa-quiz .rule{border:1px solid var(--line);border-radius:16px;padding:15px}
          .fa-quiz .rule b{display:block;margin-bottom:5px}
          .fa-quiz .btn{border:0;background:linear-gradient(145deg,var(--red),var(--red2));color:white;padding:14px 21px;border-radius:14px;font-weight:900;cursor:pointer}
          .fa-quiz .head{display:flex;justify-content:space-between;gap:12px}
          .fa-quiz .badge{display:inline-flex;padding:7px 10px;border-radius:999px;background:var(--soft);color:var(--red);font-size:12px;font-weight:900}
          .fa-quiz .cat{font-size:13px;color:var(--muted);margin-top:10px}
          .fa-quiz .q{font-size:24px;line-height:1.5;margin:24px 0}
          .fa-quiz .progress{height:8px;background:#edf0f3;border-radius:999px;overflow:hidden;margin:18px 0 28px}
          .fa-quiz .fill{height:100%;background:linear-gradient(90deg,var(--red),#df5961)}
          .fa-quiz .answers{display:grid;gap:11px}
          .fa-quiz .answer{border:1px solid var(--line);background:#fff;border-radius:16px;padding:15px;text-align:left;display:flex;gap:12px;cursor:pointer;font-size:15px;line-height:1.5}
          .fa-quiz .answer:hover{border-color:#d9a1a6}
          .fa-quiz .answer.correct{border-color:#65bd91;background:#ecfdf3}
          .fa-quiz .answer.wrong{border-color:#e49b95;background:#fff1ef}
          .fa-quiz .key{width:28px;height:28px;flex:0 0 28px;border-radius:9px;background:#f2f4f7;display:grid;place-items:center;font-weight:900}
          .fa-quiz .feedback{margin-top:18px;padding:17px;border-radius:15px;line-height:1.65}
          .fa-quiz .feedback.ok{background:#ecfdf3;color:#135f3e}
          .fa-quiz .feedback.no{background:#fff1ef;color:#8d241b}
          .fa-quiz .next{display:block;margin:18px 0 0 auto}
          .fa-quiz .summary{display:grid;grid-template-columns:repeat(5,1fr);gap:9px;margin:20px 0}
          .fa-quiz .sum{background:#f8fafc;border-radius:14px;padding:13px;text-align:center}
          .fa-quiz .sum strong{display:block;font-size:22px}
          .fa-quiz .wrong-item{border:1px solid var(--line);border-radius:16px;padding:18px;margin-top:12px}
          .fa-quiz .wrong-item>p{margin:9px 0}
          .fa-quiz .explain-box{margin-top:12px;padding:14px 15px;border-radius:14px;background:#f8fafc;border:1px solid #e6eaf0;color:#344054;line-height:1.75}
          .fa-quiz .explain-box strong{color:#18202b}
          .fa-quiz .explain-section{margin-top:10px}
          .fa-quiz .explain-label{display:inline-block;margin-right:6px;padding:3px 7px;border-radius:7px;background:#eef2f6;color:#475467;font-size:11px;font-weight:900}
          .fa-quiz .wrong-choice{color:#a12b24}
          .fa-quiz .correct-choice{color:#11663f}
          .fa-quiz .caution{font-size:13px;color:#667085}
          .fa-quiz .grade{display:inline-block;padding:9px 13px;border-radius:999px;background:var(--soft);color:var(--red);font-weight:900}
          .fa-quiz .modal{position:fixed;inset:0;background:rgba(15,23,42,.58);display:grid;place-items:center;padding:20px;z-index:99}
          .fa-quiz .modal-box{width:min(520px,100%);background:#fff;border-radius:24px;padding:30px;text-align:center;box-shadow:0 30px 80px rgba(0,0,0,.25)}
          .fa-quiz .modal-icon{font-size:54px}
          .fa-quiz .modal-box h2{font-size:28px;margin:8px 0}
          .fa-quiz .modal-box p{color:var(--muted);line-height:1.7}
          .fa-quiz .refs{margin-top:26px;border:1px solid var(--line);border-radius:16px;background:#f8fafc;padding:14px}
          .fa-quiz .refs summary{font-weight:900;cursor:pointer}
          .fa-quiz .refs p{font-size:13px;color:var(--muted);line-height:1.7}
          @media(max-width:850px){
            .fa-quiz{padding:16px}
            .fa-quiz .layout{grid-template-columns:1fr}
            .fa-quiz .side{position:static}
            .fa-quiz .rules{grid-template-columns:1fr}
            .fa-quiz .summary{grid-template-columns:repeat(2,1fr)}
            .fa-quiz .card{padding:22px}
            .fa-quiz .q{font-size:21px}
          }
          @media(max-width:480px){
            .fa-quiz{padding:12px}
            .fa-quiz .side{padding:16px}
            .fa-quiz .card{padding:16px}
            .fa-quiz h1{font-size:26px}
            .fa-quiz .q{font-size:18px}
            .fa-quiz .rule{padding:12px}
            .fa-quiz .modal-box{padding:22px}
          }
        `}</style>

        <div className="layout">
          <aside className="side">
            <div className="eyebrow">5-LEVEL ASSESSMENT</div>
            <h2>
              기능해부학
              <br />
              레벨 테스트
            </h2>
            <p className="lead">각 단계에서 10문항이 무작위 출제되며 7문항 이상 정답 시 다음 단계로 진행합니다.</p>
            <div className="stage-list" id="fa-quiz-stageList" />
            <div className="stats">
              <div className="stat">
                <strong id="fa-quiz-totalCorrect">0</strong>
                <span>총 정답</span>
              </div>
              <div className="stat">
                <strong id="fa-quiz-answered">0</strong>
                <span>응답 문항</span>
              </div>
            </div>
          </aside>
          <main className="card" id="fa-quiz-app" />
        </div>
        <div id="fa-quiz-modal" />
      </div>
    </div>
  );
}
